import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, User, Phone, Mail, MapPin, StickyNote, Banknote, QrCode,
  CheckCircle2, Package, Truck, XCircle, Clock, FileText, Trash2, Save,
} from 'lucide-react'
import { formatVND } from '@/lib/formatters'
import { useAdminOrdersStore } from '../../../state/ordersStore'
import { ORDER_STATUS_LABEL, type OrderStatus } from '../../../model/types'
import { StatusBadge } from '../../components/StatusBadge'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { useToast } from '../../components/Toast'
import { formatDate } from '../../../lib/date-fmt'

const STATUS_FLOW: { status: OrderStatus; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { status: 'pending',   label: 'Chờ xác nhận', icon: Clock },
  { status: 'confirmed', label: 'Đã xác nhận',  icon: CheckCircle2 },
  { status: 'shipping',  label: 'Đang giao',    icon: Truck },
  { status: 'delivered', label: 'Đã giao',      icon: Package },
]

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const order = useAdminOrdersStore((s) => (id ? s.getById(id) : undefined))
  const fetchOne = useAdminOrdersStore((s) => s.fetchOne)
  useEffect(() => { if (id) fetchOne(id) }, [id, fetchOne])
  const updateStatus = useAdminOrdersStore((s) => s.updateStatus)
  const addNote = useAdminOrdersStore((s) => s.addNote)
  const remove = useAdminOrdersStore((s) => s.remove)
  const push = useToast((s) => s.push)

  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [cancelConfirm, setCancelConfirm] = useState(false)
  const [note, setNote] = useState(order?.note ?? '')

  const currentIdx = useMemo(
    () => (order ? STATUS_FLOW.findIndex((s) => s.status === order.status) : -1),
    [order],
  )
  const nextStatus = useMemo(() => {
    if (!order) return null
    if (order.status === 'cancelled' || order.status === 'delivered') return null
    return STATUS_FLOW[currentIdx + 1]?.status ?? null
  }, [order, currentIdx])

  if (!order) {
    return (
      <div className="bg-surface border border-dashed border-border rounded-xl p-12 text-center">
        <div className="font-semibold text-text">Không tìm thấy đơn hàng</div>
        <Link to="/admin/don-hang" className="mt-4 inline-block text-brand-600 hover:underline">Quay lại danh sách</Link>
      </div>
    )
  }

  const fullAddress = `${order.address.street}, ${order.address.ward}, ${order.address.district}, ${order.address.province}`

  const handleAdvance = async () => {
    if (!nextStatus) return
    try {
      await updateStatus(order.id, nextStatus)
      push('success', `Đã chuyển sang "${ORDER_STATUS_LABEL[nextStatus]}"`)
    } catch (err: any) {
      push('error', err?.message ?? 'Cập nhật thất bại')
    }
  }

  const handleCancel = async () => {
    try {
      await updateStatus(order.id, 'cancelled')
      push('info', 'Đã hủy đơn hàng')
    } catch (err: any) {
      push('error', err?.message ?? 'Hủy đơn thất bại')
    }
    setCancelConfirm(false)
  }

  const handleDelete = () => {
    remove(order.id)
    push('success', 'Đã xóa đơn hàng')
    navigate('/admin/don-hang', { replace: true })
  }

  const handleSaveNote = () => {
    addNote(order.id, note)
    push('success', 'Đã lưu ghi chú')
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin/don-hang" className="text-sm text-muted hover:text-brand-600 inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-4 h-4" /> Danh sách đơn
          </Link>
          <h1 className="text-2xl font-bold text-text inline-flex items-center gap-3">
            <FileText className="w-6 h-6 text-brand-600" />
            Đơn <span className="font-mono text-brand-700">{order.code}</span>
          </h1>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted">
            <StatusBadge status={order.status} />
            <span>·</span>
            <span>{formatDate(order.createdAt)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {nextStatus && (
            <button
              onClick={handleAdvance}
              className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"
            >
              Chuyển sang: {ORDER_STATUS_LABEL[nextStatus]}
            </button>
          )}
          {order.status !== 'cancelled' && order.status !== 'delivered' && (
            <button
              onClick={() => setCancelConfirm(true)}
              className="h-11 px-5 rounded-md border border-danger/40 text-danger hover:bg-red-50 font-medium text-sm inline-flex items-center gap-2"
            >
              <XCircle className="w-4 h-4" /> Hủy đơn
            </button>
          )}
          <button
            onClick={() => setDeleteConfirm(true)}
            className="h-11 px-4 rounded-md border border-border text-muted hover:bg-slate-50 font-medium text-sm inline-flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" /> Xóa
          </button>
        </div>
      </div>

      {/* Stepper */}
      {order.status !== 'cancelled' && (
        <div className="bg-surface border border-border rounded-xl shadow-card px-6 py-5">
          <ol className="flex items-center gap-3">
            {STATUS_FLOW.map((s, i) => {
              const done = i < currentIdx
              const active = i === currentIdx
              const Icon = s.icon
              return (
                <li key={s.status} className="flex-1 flex items-center">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={[
                      'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
                      done ? 'bg-accent-500 text-white'
                        : active ? 'bg-brand-600 text-white ring-4 ring-brand-100'
                        : 'bg-slate-100 text-muted',
                    ].join(' ')}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className={[
                      'text-sm font-semibold hidden sm:block',
                      active ? 'text-brand-700' : done ? 'text-accent-600' : 'text-muted',
                    ].join(' ')}>{s.label}</div>
                  </div>
                  {i < STATUS_FLOW.length - 1 && (
                    <div className={[
                      'flex-1 h-0.5 mx-3 rounded-full',
                      i < currentIdx ? 'bg-accent-500' : 'bg-slate-200',
                    ].join(' ')} />
                  )}
                </li>
              )
            })}
          </ol>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5">
        <div className="space-y-5">
          {/* Items */}
          <section className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
            <div className="h-14 px-5 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-text">Sản phẩm ({order.items.length})</h3>
            </div>
            <div className="divide-y divide-border">
              {order.items.map((it) => (
                <div key={it.id} className="px-5 py-4 flex gap-3">
                  <div className="w-16 h-16 shrink-0 rounded-md overflow-hidden bg-slate-100 border border-border">
                    <img
                      src={it.image}
                      alt={it.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="%23e2e8f0"/></svg>'
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/san-pham/${it.slug}`} target="_blank" className="text-sm font-medium text-text hover:text-brand-600 line-clamp-2">
                      {it.name}
                    </Link>
                    <div className="text-xs text-muted mt-0.5">{it.brand} · SL: {it.qty}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-brand-700 font-semibold text-sm">{formatVND(it.price)}</span>
                      {it.oldPrice > it.price && (
                        <span className="text-muted text-xs line-through">{formatVND(it.oldPrice)}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold text-text">{formatVND(it.price * it.qty)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="px-5 py-4 bg-slate-50 border-t border-border space-y-1.5 text-sm">
              <div className="flex items-center justify-between text-muted">
                <span>Tạm tính</span>
                <span className="text-text">{formatVND(order.subtotal)}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-accent-600">Giảm giá</span>
                  <span className="text-accent-600">- {formatVND(order.discount)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-muted">
                <span>Phí vận chuyển</span>
                <span className="text-text">{order.shippingFee === 0 ? 'Miễn phí' : formatVND(order.shippingFee)}</span>
              </div>
              <div className="flex items-center justify-between pt-2 mt-2 border-t border-border">
                <span className="font-semibold">Tổng cộng</span>
                <span className="font-bold text-brand-700 text-xl">{formatVND(order.grandTotal)}</span>
              </div>
            </div>
          </section>

          {/* Timeline */}
          <section className="bg-surface border border-border rounded-xl shadow-card p-5">
            <h3 className="font-semibold text-text mb-4">Lịch sử trạng thái</h3>
            <ol className="relative border-l-2 border-slate-200 ml-2 space-y-5">
              {order.statusHistory.map((h, i) => (
                <li key={i} className="pl-5 relative">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-brand-600 ring-4 ring-brand-100" />
                  <div className="flex items-center gap-2">
                    <StatusBadge status={h.status} />
                    <span className="text-xs text-muted">{formatDate(h.at)}</span>
                  </div>
                  {h.note && <div className="text-sm text-muted mt-1">{h.note}</div>}
                </li>
              ))}
            </ol>
          </section>

          {/* Note */}
          <section className="bg-surface border border-border rounded-xl shadow-card p-5">
            <h3 className="font-semibold text-text mb-3">Ghi chú nội bộ</h3>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              className="w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm outline-none focus:border-brand-500 resize-none"
              placeholder="Ghi chú cho đội vận hành..."
            />
            <div className="mt-3 flex items-center justify-end">
              <button
                onClick={handleSaveNote}
                className="h-10 px-4 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"
              >
                <Save className="w-4 h-4" /> Lưu ghi chú
              </button>
            </div>
          </section>
        </div>

        <div className="space-y-5">
          {/* Customer */}
          <section className="bg-surface border border-border rounded-xl shadow-card p-5">
            <h3 className="font-semibold text-text mb-3">Khách hàng</h3>
            <ul className="space-y-2.5 text-sm">
              <li className="flex items-start gap-2.5">
                <User className="w-4 h-4 text-brand-600 mt-0.5 shrink-0" />
                <span className="font-medium text-text">{order.address.fullName}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="w-4 h-4 text-brand-600 mt-0.5 shrink-0" />
                <a href={`tel:${order.address.phone}`} className="text-text hover:text-brand-600">{order.address.phone}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 text-brand-600 mt-0.5 shrink-0" />
                <a href={`mailto:${order.address.email}`} className="text-text hover:text-brand-600 break-all">{order.address.email}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-brand-600 mt-0.5 shrink-0" />
                <span className="text-text">{fullAddress}</span>
              </li>
              {order.address.note && (
                <li className="flex items-start gap-2.5 pt-2 border-t border-border">
                  <StickyNote className="w-4 h-4 text-brand-600 mt-0.5 shrink-0" />
                  <span className="text-muted italic">{order.address.note}</span>
                </li>
              )}
            </ul>
          </section>

          {/* Payment */}
          <section className="bg-surface border border-border rounded-xl shadow-card p-5">
            <h3 className="font-semibold text-text mb-3">Thanh toán</h3>
            <div className="flex items-center gap-2">
              {order.payment === 'cod'
                ? <><Banknote className="w-5 h-5 text-brand-600" /> <span className="font-medium">Thanh toán khi nhận hàng (COD)</span></>
                : <><QrCode  className="w-5 h-5 text-brand-600" /> <span className="font-medium">Chuyển khoản QR VietQR</span></>
              }
            </div>
            <div className="mt-3 text-sm text-muted">
              Tổng cần thu: <span className="font-bold text-brand-700">{formatVND(order.grandTotal)}</span>
            </div>
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={cancelConfirm}
        title="Hủy đơn hàng"
        message={`Chắc chắn hủy đơn ${order.code}? Trạng thái sẽ chuyển sang "Đã hủy".`}
        confirmLabel="Hủy đơn"
        onConfirm={handleCancel}
        onCancel={() => setCancelConfirm(false)}
      />

      <ConfirmDialog
        open={deleteConfirm}
        title="Xóa đơn hàng"
        message={`Ẩn đơn ${order.code} khỏi danh sách (chỉ local, server vẫn giữ)?`}
        confirmLabel="Xóa"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(false)}
      />
    </div>
  )
}
